/**
 * GroupNode — Container node that holds child nodes rendered at full size by ReactFlow.
 *
 * Unlike designer nodes, GroupNode does NOT use DesignerNodeShell. It has its own UI pattern:
 * a dark container (#141414) with an editable floating label. Children render as their actual
 * ReactFlow node components (e.g., ImageGeneratorNode) at full size inside the container via
 * the `parentId` mechanism — no scaling, no thumbnail cards.
 */

import React, { memo, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { Handle, Position, type NodeProps, useReactFlow, useStore } from 'reactflow'
import { ChevronDown, Palette, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { localizeDefaultNodeName } from '../shared/defaultNodeNames'
import { FloatingNodeLabel } from '../shared'
import { NODE_CARD_RADIUS_CLASS, NODE_CARD_RADIUS_PX } from '../shared/constants'
import { resolveReservedMediaBox, type PersistedMediaRefLike, type ReservedMediaBoxSource } from '../shared/reservedMediaBox'
import { AgentNodeToolbar, type ToolbarAction } from '../shared/AgentNodeToolbar'
import { AddToChatIcon } from '../shared/AddToChatIcon'
import { useSetHoveredNodeId } from '../../NodeHoverContext'
import { isBlockingOverlayOpen } from '../../../../hooks/canvasShortcutGuard'
import { useCanvasOptional } from '../../CanvasContext'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { useNodeActions } from '../shared/useNodeActions'
import { isGroupableNodeType } from './groupValidation'
import { getNodeDimensions } from '../../nodeRegistry'
import { isShotListLayout, SHOT_LIST_NODE_DIMS } from '../StoryboardDesignerNode/shotListLayout'
import { getTypedNodeData, type ReactFlowCanvasNodeDataByType } from '../../../../generated/nodeDataTypes'
import {
  dataStrOpt,
  type ReactFlowCanvasEdgeData,
  type ReactFlowCanvasNode,
  type ReactFlowCanvasNodeData,
} from '../../../../hooks/useYCanvas'
import { useGroupDropTarget } from './GroupDropTargetContext'
import { AddPanelButton, AddPlaceholderSlot } from './AddPanelButton'
import { GroupDeleteDialog } from './GroupDeleteDialog'
import { ReferenceSelector, type SelectedNodeReference } from '../shared/ReferenceSelector'
import { useUploadFile } from '../../../../contexts/UploadContext'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { NODE_BG_SWATCH_COLORS, resolveNodeBgColor, localizeNodeBgColor } from '../shared/nodeBgColors'
import { useCanvasNodeChromeMode } from '../../CanvasNodeChromeModeContext'

const layoutLog = createDebug('dw:layout:dimension-sync')
const groupAddLog = createDebug('dw:group:add-position')
// Structured NAD trace for group deletion. Both the keyboard Delete/Backspace
// shortcut and the toolbar Delete button funnel through the same dialog-gated
// path; the `trigger` field lets a reviewer confirm both entry points reach the
// identical downstream removal. Dev-gated behind VITE_NAD_DEBUG (zero prod cost).
const groupDeleteLog = createNadDebug('dw:group-delete')
// `nad trace --feature canvas-shortcut-overlay-guard` — emitted when group
// deletion via keyboard is suppressed because a blocking overlay is open.
const overlayGuardLog = createNadDebug('dw:canvas-shortcut-overlay-guard')
// Structured NAD trace for grid cell sizing. Per child it records whether the
// cell height came from the measured DOM (`branch=measured`, footer already in
// the height) or the schema fallback (`branch=fallback`, footer added from
// schema `footerHeight`). This is the load-bearing decision behind the
// node-group vertical-overlap fix: the fallback must reserve the QuickInfoBar
// footer (footerHeight=64) so a lower grid row does not overlap the card above.
// Feature tag `group-grid-spacing`. Dev-gated behind VITE_NAD_DEBUG (zero prod cost).
const cellSizeLog = createNadDebug('dw:group-grid-spacing:cell-size')
// Structured NAD trace for adding existing canvas nodes into a manual group.
// Dev-gated behind VITE_NAD_DEBUG (zero prod cost).
const groupNodeAddLog = createNadDebug('dw:group-node-add')
// Structured NAD trace for the parent-before-child topological reorder applied
// at the end of deriveGroupedNodeLayout. ReactFlow v11 drops a parented child
// that appears before its parent in the nodes array; the live drag-to-group
// "absorb" path used to set parentId without reordering, so a node dragged into
// a group created AFTER it would vanish until a refresh re-ran the sorted
// from-YDoc rebuild. This trace fires ONLY when an actual reorder happens (the
// no-op common case is silent) so a reviewer can confirm the load-bearing
// branch triggered. Feature tag `group-topo-order`. Dev-gated (zero prod cost).
const topoOrderLog = createNadDebug('dw:group-topo-order')

export type LayoutMode = 'horizontal' | 'vertical' | 'auto'
export const DERIVED_GROUP_NODE_TYPES = new Set(['nodeGroup', 'webtoonGroup', 'shortVideoGroup'])
export type GroupChildNodeIds = Array<string | null>
const EMPTY_GROUP_CHILD_NODE_IDS: GroupChildNodeIds = []

function GroupEdgeProxyHandles() {
  return (
    <>
      <Handle
        type="target"
        position={Position.Left}
        className="!bg-transparent !border-0 !w-1 !h-4"
        style={{ pointerEvents: 'none' }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!bg-transparent !border-0 !w-1 !h-4"
        style={{ pointerEvents: 'none' }}
      />
    </>
  )
}

/**
 * Returns the row and column indices for a child at the given index,
 * based on the layout mode. Used to filter between-item add buttons
 * so they only appear between items in the same row (horizontal/auto)
 * or same column (vertical).
 */
export function getRowCol(index: number, layoutMode: LayoutMode): { row: number; col: number } {
  switch (layoutMode) {
    case 'horizontal':
      return { row: 0, col: index }
    case 'vertical':
      return { row: index, col: 0 }
    case 'auto':
    default:
      return { row: Math.floor(index / COLUMNS), col: index % COLUMNS }
  }
}

export interface GroupNodeData {
  in_label?: string
  label?: string
  in_child_node_ids?: GroupChildNodeIds
  in_layout_mode?: string
  in_collapsed?: boolean
  in_collapsible?: boolean
  in_bg_color?: string
}

export type CanvasNodeData = ReactFlowCanvasNodeData
export type CanvasNode = ReactFlowCanvasNode
type NodeGroupReactFlowData = ReactFlowCanvasNodeDataByType['nodeGroup']

type GroupRenderNodeSummary = {
  id: string
  type?: string
  data?: CanvasNodeData
  position: { x: number; y: number }
  parentId?: string
  width?: number | null
  height?: number | null
  // Tracked because computeCellSize ignores a selected child's measurement.
  // Without it here the layout cannot see the flag and the grid keeps
  // resizing itself around selection chrome. This costs no extra renders in
  // practice: selecting a card already changes its measured height, which
  // this selector was tracking anyway.
  selected?: boolean
}

type GroupStoreState = {
  getNodes: () => GroupRenderNodeSummary[]
}

function summarizeGroupRenderNodes(nodes: GroupRenderNodeSummary[]): GroupRenderNodeSummary[] {
  return nodes.map((node) => ({
    id: node.id,
    type: node.type,
    data: node.data,
    position: { x: node.position.x, y: node.position.y },
    parentId: node.parentId,
    width: node.width,
    height: node.height,
    selected: node.selected,
  }))
}

export function areGroupRenderNodeSummariesEqual(
  prev: GroupRenderNodeSummary[],
  next: GroupRenderNodeSummary[],
): boolean {
  if (prev === next) return true
  if (prev.length !== next.length) return false

  for (let i = 0; i < prev.length; i++) {
    const a = prev[i]
    const b = next[i]
    if (a.id !== b.id) return false
    if (a.type !== b.type) return false
    if (a.data !== b.data) return false
    if (a.parentId !== b.parentId) return false
    if (a.width !== b.width) return false
    if (a.height !== b.height) return false
    // Deliberately NOT comparing `selected`. The flag is carried on the
    // summary so computeCellSize can read it, but a selection change on its
    // own must not trigger a relayout: ReactFlow clears `selected` a frame
    // before the card shrinks back, so recomputing on that flip would read
    // the still-inflated measurement as an unselected node's true height and
    // restack the group for one frame. Recomputing on the height change
    // alone is both sufficient and correctly ordered — by the time a card
    // measures taller, `selected` is already true.
    if (a.position.x !== b.position.x) return false
    if (a.position.y !== b.position.y) return false
  }

  return true
}

function selectGroupRenderNodes(state: GroupStoreState): GroupRenderNodeSummary[] {
  return summarizeGroupRenderNodes(state.getNodes())
}

export function normalizeLayoutMode(value: string | undefined): LayoutMode {
  switch (value) {
    case 'horizontal':
    case 'vertical':
    case 'auto':
      return value
    default:
      return 'auto'
  }
}

export function getGroupNodeData(
  node: { type?: string | null; data?: object },
): GroupNodeData | undefined {
  switch (node.type) {
    case 'nodeGroup':
      return getTypedNodeData(node, 'nodeGroup')
    case 'shortVideoGroup':
      return getTypedNodeData(node, 'shortVideoGroup')
    case 'webtoonGroup':
      return getTypedNodeData(node, 'webtoonGroup')
    default:
      return undefined
  }
}

function firstNonEmptyString(...values: Array<string | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim()
    if (trimmed) return trimmed
  }
  return undefined
}

function clipNumberDisplayName(clipNumber?: number): string | undefined {
  return typeof clipNumber === 'number' && Number.isFinite(clipNumber) && clipNumber > 0
    ? `Clip ${clipNumber}`
    : undefined
}

export function getCanvasNodeDisplayName(
  node: { type?: string | null; data?: object },
  fallback = 'Node',
): string {
  switch (node.type) {
    case 'characterDesigner': {
      const data = getTypedNodeData(node, 'characterDesigner')
      return firstNonEmptyString(data?.in_name, data?.label) ?? fallback
    }
    case 'debugHang': {
      const data = getTypedNodeData(node, 'debugHang')
      return firstNonEmptyString(data?.in_label, data?.label) ?? fallback
    }
    case 'imageGenerator': {
      const data = getTypedNodeData(node, 'imageGenerator')
      return firstNonEmptyString(data?.in_name, data?.label) ?? fallback
    }
    case 'locationDesigner': {
      const data = getTypedNodeData(node, 'locationDesigner')
      return firstNonEmptyString(data?.in_name, data?.label) ?? fallback
    }
    case 'nodeGroup':
    case 'shortVideoGroup':
    case 'webtoonGroup': {
      const data = getGroupNodeData(node)
      return firstNonEmptyString(data?.in_label, data?.label) ?? 'Group'
    }
    case 'objectDesigner': {
      const data = getTypedNodeData(node, 'objectDesigner')
      return firstNonEmptyString(data?.in_name, data?.label) ?? fallback
    }
    case 'scriptWriter': {
      const data = getTypedNodeData(node, 'scriptWriter')
      return firstNonEmptyString(data?.in_name, data?.label) ?? fallback
    }
    case 'soundExpert': {
      const data = getTypedNodeData(node, 'soundExpert')
      return firstNonEmptyString(data?.in_name, data?.label) ?? fallback
    }
    case 'videoGenerator': {
      const data = getTypedNodeData(node, 'videoGenerator')
      return firstNonEmptyString(
        data?.in_name,
        clipNumberDisplayName(data?.in_clip_number),
        data?.label,
      ) ?? fallback
    }
    case 'webtoonPanelGenerator': {
      const data = getTypedNodeData(node, 'webtoonPanelGenerator')
      return firstNonEmptyString(data?.in_name, data?.label) ?? fallback
    }
    default:
      return node.type ?? fallback
  }
}

function getRequestedAspectRatio(node: { type?: string | null; data?: object }): string | undefined {
  switch (node.type) {
    case 'imageGenerator':
      return getTypedNodeData(node, 'imageGenerator')?.in_aspect_ratio
    case 'storyboardDesigner':
      return getTypedNodeData(node, 'storyboardDesigner')?.in_aspect_ratio
    case 'videoGenerator':
      return getTypedNodeData(node, 'videoGenerator')?.in_aspect_ratio
    case 'webtoonPanelGenerator':
      return getTypedNodeData(node, 'webtoonPanelGenerator')?.in_aspect_ratio
    default:
      return undefined
  }
}

/** Persisted asset ref of an uploaded-media child (`image` / `video`), or
 *  undefined for every other node type. Upload nodes have no requested aspect
 *  ratio; their box comes from the upload metadata instead. */
function getUploadedMediaRef(node: { type?: string | null; data?: object }): PersistedMediaRefLike | undefined {
  switch (node.type) {
    case 'image':
      return getTypedNodeData(node, 'image')?.out_image
    case 'video':
      return getTypedNodeData(node, 'video')?.out_video
    default:
      return undefined
  }
}

/** Where a child's layout box came from — `measured` or a ReservedMediaBoxSource
 *  for upload nodes, `schema-layout` for every other unmeasured type. Logged on
 *  `dw:group-grid-spacing:cell-size` so a cell-size flip names its cause. */
export type ChildLayoutSource = 'measured' | ReservedMediaBoxSource | 'schema-layout'

/**
 * Layout box for one group child. Measured DOM size wins. Unmeasured upload
 * nodes use the same `resolveReservedMediaBox` the node card renders with, so
 * the cell the group reserves before a child mounts equals the box it renders
 * once it does — no re-layout when `onlyRenderVisibleElements` remounts it
 * during a pan. Other types use the schema layout for their requested ratio.
 */
export function resolveChildLayoutDims(
  node: { type?: string | null; data?: object },
  measured: { width: number; height: number } | null,
): { dims: { width: number; height: number; footerHeight: number }; source: ChildLayoutSource } {
  // Measured DOM height already includes any in-flow footer (e.g. QuickInfoBar),
  // so footerHeight is 0 there. The fallback uses the schema layout (preview
  // only) plus schema footerHeight to reserve the footer the card will render.
  if (measured) return { dims: { ...measured, footerHeight: 0 }, source: 'measured' }
  if (isShotListLayout(node)) {
    return { dims: { ...SHOT_LIST_NODE_DIMS, footerHeight: 0 }, source: 'schema-layout' }
  }
  const schemaDims = getNodeDimensions(node.type ?? '', getRequestedAspectRatio(node))
  if (node.type === 'image' || node.type === 'video') {
    const box = resolveReservedMediaBox(getUploadedMediaRef(node))
    return {
      dims: { width: box.width, height: box.height, footerHeight: schemaDims.footerHeight },
      source: box.source,
    }
  }
  return { dims: schemaDims, source: 'schema-layout' }
}

// Child layout constants
export const CHILD_WIDTH = 315     // default ImageGeneratorNode width (16:9)
export const CHILD_HEIGHT = 300    // estimated child height
export const CHILD_GAP = 24
export const COLUMNS = 3
export const CONTAINER_PADDING = 32
export const COLLAPSED_GROUP_WIDTH = 315
export const COLLAPSED_GROUP_HEIGHT = 48
/** Height of the FloatingNodeLabel above each child node (text-sm ~20px + marginBottom 6px) */
export const CHILD_LABEL_HEIGHT = 26


/**
 * Compute the cell size for a group's children by finding the maximum
 * width and full height across all child nodes. Full height includes
 * the image/content area (layout.height), the floating label above
 * (CHILD_LABEL_HEIGHT), and any per-type footer (footerHeight from schema).
 * Falls back to CHILD_WIDTH/CHILD_HEIGHT when the group is empty or
 * children have no schema.
 */
/**
 * Each child's height as last measured while it was NOT selected.
 *
 * A selected card measures taller than the same card unselected, and
 * cellHeight is the max across a group, so a selected card's measurement
 * must never size the grid. Falling back to the schema size is not enough on
 * its own: select every child of a group — one marquee drag — and there is
 * no unselected sibling left holding the cell at its real size, so the whole
 * grid would collapse to the schema estimate and restack. Remembering the
 * unselected measurement keeps the pitch identical for any selection, from
 * one child to all of them.
 *
 * Keyed by node id and self-rebuilding, so a stale entry costs nothing but a
 * little memory; the size guard below stops an id-churning session from
 * growing it without bound.
 */
const lastUnselectedChildHeight = new Map<string, number>()
const LAST_UNSELECTED_HEIGHT_CACHE_LIMIT = 4096

/** Test seam: the cache is module state, so suites must be able to clear it. */
export function resetUnselectedChildHeightCache(): void {
  lastUnselectedChildHeight.clear()
}

export function computeCellSize(
  childNodeIds: (string | null)[],
  allNodes: Array<{
    id: string
    type?: string
    data?: CanvasNodeData
    width?: number | null
    height?: number | null
    selected?: boolean
  }>,
): { cellWidth: number; cellHeight: number; panelHeight: number } {
  const actualIds = childNodeIds.filter((id): id is string => id !== null)
  if (actualIds.length === 0) {
    return { cellWidth: CHILD_WIDTH, cellHeight: CHILD_HEIGHT, panelHeight: CHILD_HEIGHT }
  }

  let maxWidth = 0
  let maxHeight = 0
  let maxPanelHeight = 0

  for (const childId of actualIds) {
    const node = allNodes.find((n) => n.id === childId)
    if (!node?.type) continue

    const measuredWidth = typeof node.width === 'number' && node.width > 0 ? node.width : null
    const measuredHeight = typeof node.height === 'number' && node.height > 0 ? node.height : null

    // Size the cell from this child's height as measured while UNSELECTED.
    //
    // Selecting a card swaps its body from the lightweight FlatIdleNodeCard
    // shell to the full card, which is taller — measured on a real project at
    // 395 -> 458, because the shell reserves the QuickInfoBar (80) but not the
    // error footer the full card paints (65). cellHeight is the MAX over all
    // children, so clicking one errored clip grew the row pitch by 63 and
    // restacked every row below it: row N moved N x 63px. Users reported that
    // as "the clips move out of position when I drag them", but a staged
    // browser probe put the whole displacement on the click — the drag moved
    // nothing at all.
    //
    // Selection chrome is transient UI, not content, so it must not size the
    // grid. Reading through the cache rather than simply skipping selected
    // children also means the pitch never depends on HOW MANY are selected,
    // and that deselection cannot flash: ReactFlow clears `selected` a frame
    // before the card shrinks back, so the inflated measurement is briefly
    // attributable to an unselected node, and it must not be believed then
    // either. The cost is that a selected card overhangs its cell, which is
    // benign — ReactFlow elevates the selected node above its neighbours.
    const isSelected = node.selected === true
    if (!isSelected && measuredHeight !== null) {
      if (lastUnselectedChildHeight.size >= LAST_UNSELECTED_HEIGHT_CACHE_LIMIT) {
        lastUnselectedChildHeight.clear()
      }
      lastUnselectedChildHeight.set(childId, measuredHeight)
    }
    const stableHeight = isSelected
      ? (lastUnselectedChildHeight.get(childId) ?? null)
      : measuredHeight
    const isMeasured = measuredWidth !== null && stableHeight !== null
    const { dims, source } = resolveChildLayoutDims(node, isMeasured ? { width: measuredWidth, height: stableHeight } : null)

    if (dims.width > maxWidth) maxWidth = dims.width
    const fullHeight = dims.height + CHILD_LABEL_HEIGHT + dims.footerHeight
    // Object form: the NAD forwarder maps printf `%c` style args onto format
    // keys, which shifted every value of the old printf line by two keys.
    cellSizeLog({
      event: 'child',
      child_id: childId,
      type: node.type ?? '',
      branch: isMeasured ? 'measured' : 'fallback',
      source,
      layout_w: dims.width,
      layout_h: dims.height,
      footer_h: dims.footerHeight,
      full_h: fullHeight,
    })
    if (fullHeight > maxHeight) maxHeight = fullHeight
    // Panel "card" = preview artwork + per-type footer chrome (handles, action
    // bar). Excludes only the FloatingNodeLabel that renders ABOVE the card,
    // not inside it. This matches the actual rendered card bounds so the
    // dashed placeholder visually mirrors a real panel.
    const panelCardHeight = dims.height + dims.footerHeight
    if (panelCardHeight > maxPanelHeight) maxPanelHeight = panelCardHeight
  }

  if (maxWidth === 0 || maxHeight === 0) {
    return { cellWidth: CHILD_WIDTH, cellHeight: CHILD_HEIGHT, panelHeight: CHILD_HEIGHT }
  }

  return { cellWidth: maxWidth, cellHeight: maxHeight, panelHeight: maxPanelHeight }
}

/**
 * Compute the position {x, y} for a child at the given index
 * based on the active layout mode.
 */
export function computeChildPosition(
  index: number,
  layoutMode: LayoutMode,
  cellWidth: number = CHILD_WIDTH,
  cellHeight: number = CHILD_HEIGHT,
): { x: number; y: number } {
  let col: number
  let row: number

  switch (layoutMode) {
    case 'horizontal':
      col = index
      row = 0
      break
    case 'vertical':
      col = 0
      row = index
      break
    case 'auto':
    default:
      col = index % COLUMNS
      row = Math.floor(index / COLUMNS)
      break
  }

  return {
    x: CONTAINER_PADDING + col * (cellWidth + CHILD_GAP),
    y: CONTAINER_PADDING + CHILD_LABEL_HEIGHT + row * (cellHeight + CHILD_GAP),
  }
}

/**
 * Compute the container {width, height} for the given child count
 * and layout mode.
 */
export function computeContainerSize(
  childCount: number,
  layoutMode: LayoutMode,
  cellWidth: number = CHILD_WIDTH,
  cellHeight: number = CHILD_HEIGHT,
): { width: number; height: number } {
  const count = childCount || 1 // minimum 1 for empty state

  let cols: number
  let rows: number

  switch (layoutMode) {
    case 'horizontal':
      cols = count
      rows = 1
      break
    case 'vertical':
      cols = 1
      rows = count
      break
    case 'auto':
    default:
      cols = Math.min(count, COLUMNS)
      rows = Math.max(Math.ceil(count / COLUMNS), 1)
      break
  }

  return {
    width: 2 * CONTAINER_PADDING + cols * cellWidth + (cols - 1) * CHILD_GAP,
    height: 2 * CONTAINER_PADDING + CHILD_LABEL_HEIGHT + rows * cellHeight + (rows - 1) * CHILD_GAP,
  }
}

/**
 * Compute the new in_child_node_ids array after removing a child.
 * Always replaces the removed child with null (preserving the gap).
 * Trailing nulls are trimmed.
 */
export function removeChildFromIds(
  childId: string,
  childNodeIds: (string | null)[],
): (string | null)[] {
  const newIds = childNodeIds.map((id) => (id === childId ? null : id))

  // Trim trailing nulls
  while (newIds.length > 0 && newIds[newIds.length - 1] === null) {
    newIds.pop()
  }

  return newIds
}

type GroupLayoutNodeLike = Pick<
  CanvasNode,
  'id' | 'type' | 'data' | 'position' | 'parentId' | 'style' | 'hidden'
> & {
  width?: number | null
  height?: number | null
  // Read by computeCellSize to ignore a selected child's measurement; see
  // the note there on why selection must not size the grid.
  selected?: boolean
}

export function isDerivedGroupNodeType(type?: string): boolean {
  return type ? DERIVED_GROUP_NODE_TYPES.has(type) : false
}

/**
 * Stable topological ordering so every parent precedes its children in the
 * nodes array.
 *
 * ReactFlow v11 silently drops a parented child node that appears BEFORE its
 * parent in the array. The from-YDoc rebuild (`useYCanvas`) already sorts for
 * this, but live mutation paths (drag-to-group absorb, cross-group transfer,
 * ReferenceSelector absorb) only set `parentId` and preserve array order — so a
 * node dragged into a group that was created after it would vanish until a page
 * refresh. Folding the reorder into `deriveGroupedNodeLayout` (the single
 * authoritative layout helper every path runs through) fixes all of them at
 * once and keeps one source of truth.
 *
 * Uses depth (number of `parentId` hops to a root) rather than a two-way
 * roots-vs-children partition so nested groups (a group that is itself a child)
 * order correctly. A missing parent (not in this array) or a `parentId` cycle
 * terminates the depth walk early rather than looping forever, so the result is
 * always finite and never drops a node — mirroring the MISSING-child tolerance
 * in `deriveGroupedNodeLayout`.
 *
 * Ref-stability: returns the SAME array reference when the input is already
 * topologically ordered (the overwhelmingly common case), so it composes with
 * `deriveGroupedNodeLayout`'s no-mutation fast path and never forces a needless
 * ReactFlow re-render. A new array is allocated only when an actual reorder is
 * required.
 */
export function sortParentsBeforeChildren<
  T extends Pick<GroupLayoutNodeLike, 'id' | 'parentId'>,
>(nodes: T[]): T[] {
  if (nodes.length < 2) return nodes

  const indexById = new Map<string, number>()
  for (let i = 0; i < nodes.length; i++) indexById.set(nodes[i].id, i)

  // Memoized depth = hops to a root via parentId. Guards against a missing
  // parent (not in this array) and against cycles (visited set) by ending the
  // walk at the unresolved hop — the result stays finite (never infinite-loops
  // and never drops a node); exact depth in a cycle is unimportant since the
  // only requirement is a stable, parent-before-child ordering.
  const depthByIndex = new Map<number, number>()
  const depthOf = (index: number): number => {
    const memo = depthByIndex.get(index)
    if (memo !== undefined) return memo
    const visiting = new Set<number>()
    let cursor = index
    let depth = 0
    let walking = true
    while (walking) {
      const cached = depthByIndex.get(cursor)
      if (cached !== undefined) {
        depth += cached
        walking = false
        break
      }
      const parentId = nodes[cursor].parentId
      const parentIndex = parentId ? indexById.get(parentId) : undefined
      if (parentIndex === undefined || visiting.has(parentIndex)) {
        walking = false
        break
      }
      visiting.add(cursor)
      cursor = parentIndex
      depth += 1
    }
    depthByIndex.set(index, depth)
    return depth
  }

  // Validity gate (ref-stability): if every node's parent already precedes it,
  // the array is valid — return the same reference, no allocation.
  let needsReorder = false
  for (let i = 0; i < nodes.length; i++) {
    const parentId = nodes[i].parentId
    if (!parentId) continue
    const parentIndex = indexById.get(parentId)
    if (parentIndex !== undefined && parentIndex > i) {
      needsReorder = true
      break
    }
  }
  if (!needsReorder) return nodes

  // Stable sort by (depth asc, originalIndex asc). Depth-ascending guarantees a
  // parent (lower depth) precedes its children across arbitrary nesting; the
  // originalIndex tiebreak preserves sibling order (driven by in_child_node_ids,
  // not array order). Array.prototype.sort is stable in modern engines.
  const order = nodes.map((_, i) => i)
  order.sort((a, b) => depthOf(a) - depthOf(b) || a - b)
  const sorted = order.map((i) => nodes[i])

  const movedNodeIds = order
    .map((origIndex, newIndex) => (origIndex !== newIndex ? nodes[origIndex].id : null))
    .filter((id): id is string => id !== null)
  topoOrderLog({
    event: 'topo-reorder',
    nodeCount: nodes.length,
    movedNodeIds,
  })

  return sorted
}

/**
 * Derive grouped child positions from parentId + in_child_node_ids.
 *
 * This is the authoritative frontend layout model for grouped children.
 * Any live mutation path that changes group membership/order should run
 * through this helper so it matches the YDoc rebuild path.
 */
export function deriveGroupedNodeLayout<T extends GroupLayoutNodeLike>(
  nodes: T[],
  groupIds?: Iterable<string>,
): T[] {
  const targetGroups = groupIds ? new Set(groupIds) : null
  // Pure layout: operate directly on the input nodes. Deletion is YDoc-first,
  // so a just-deleted node is simply absent from `nodes` (it never reaches
  // here), and a child missing from `nodes` is skipped via the MISSING-child
  // continue below — there is no suppression band-aid.
  const liveNodes = nodes
  const indexById = new Map(liveNodes.map((node, index) => [node.id, index]))
  const nodeSummaries = liveNodes.map((node) => ({
    id: node.id,
    type: node.type,
    data: node.data,
    width: node.width,
    height: node.height,
    selected: node.selected,
  }))

  let nextNodes = liveNodes
  let mutated = false
  const ensureMutable = () => {
    if (!mutated) {
      nextNodes = [...liveNodes]
      mutated = true
    }
  }

  for (let groupIndex = 0; groupIndex < nextNodes.length; groupIndex++) {
    const groupNode = nextNodes[groupIndex]
    if (!isDerivedGroupNodeType(groupNode.type)) continue
    if (targetGroups && !targetGroups.has(groupNode.id)) continue

    const groupData: GroupNodeData = getGroupNodeData(groupNode) ?? {}
    const childIds = groupData.in_child_node_ids ?? []
    const layoutMode = normalizeLayoutMode(groupData.in_layout_mode)
    const { cellWidth, cellHeight } = computeCellSize(childIds, nodeSummaries)
    const collapsed = groupNode.type === 'nodeGroup'
      ? groupData.in_collapsible === true && groupData.in_collapsed === true
      : groupData.in_collapsed === true

    groupAddLog('deriveLayout groupId=%s childIds=%o layout=%s cellW=%d cellH=%d collapsed=%s',
      groupNode.id, childIds, layoutMode, cellWidth, cellHeight, collapsed)

    if (groupNode.type === 'nodeGroup') {
      const expandedSize = computeContainerSize(
        childIds.length,
        layoutMode,
        cellWidth,
        cellHeight,
      )
      const width = collapsed ? COLLAPSED_GROUP_WIDTH : expandedSize.width
      const height = collapsed ? COLLAPSED_GROUP_HEIGHT : expandedSize.height
      groupAddLog('deriveLayout containerSize groupId=%s childCount=%d w=%d h=%d (was w=%s h=%s)',
        groupNode.id, childIds.length, width, height,
        (groupNode.style as Record<string, unknown>)?.width, (groupNode.style as Record<string, unknown>)?.height)
      const currentStyle = groupNode.style || {}
      if (currentStyle.width !== width || currentStyle.height !== height) {
        ensureMutable()
        nextNodes[groupIndex] = {
          ...groupNode,
          style: { ...currentStyle, width, height },
        }
      }
    }

    for (let childIndex = 0; childIndex < childIds.length; childIndex++) {
      const childId = childIds[childIndex]
      if (childId === null) continue

      const nodeIndex = indexById.get(childId)
      if (nodeIndex === undefined) {
        groupAddLog('deriveLayout MISSING child groupId=%s childId=%s idx=%d — not in nodes array',
          groupNode.id, childId, childIndex)
        continue
      }

      const childNode = nextNodes[nodeIndex]
      if (childNode.parentId !== groupNode.id) {
        groupAddLog('deriveLayout WRONG_PARENT groupId=%s childId=%s idx=%d parentId=%s',
          groupNode.id, childId, childIndex, childNode.parentId)
        continue
      }

      const expectedPos = computeChildPosition(
        childIndex,
        layoutMode,
        cellWidth,
        cellHeight,
      )
      const nextHidden = collapsed
      const shouldUpdatePosition =
        childNode.position.x !== expectedPos.x || childNode.position.y !== expectedPos.y
      const shouldUpdateHidden = childNode.hidden !== nextHidden

      if (shouldUpdatePosition) {
        groupAddLog('deriveLayout childPos groupId=%s childId=%s idx=%d expected=(%d,%d) actual=(%d,%d) parentId=%s',
          groupNode.id, childId, childIndex, expectedPos.x, expectedPos.y,
          childNode.position.x, childNode.position.y, childNode.parentId)
      }

      if (!shouldUpdatePosition && !shouldUpdateHidden) continue

      ensureMutable()
      nextNodes[nodeIndex] = {
        ...childNode,
        position: expectedPos,
        hidden: nextHidden,
      }
    }
  }

  // Enforce parent-before-child array order so ReactFlow renders parented
  // children (it drops a child that precedes its parent). Ref-stable: returns
  // `nextNodes` unchanged when the order is already valid.
  return sortParentsBeforeChildren(nextNodes)
}

const GroupNode = memo(function GroupNode(props: NodeProps<NodeGroupReactFlowData>) {
  const { id, data, selected, dragging } = props
  const { t } = useTranslation('canvas')
  const { setNodes, getNode, getNodes } = useReactFlow<CanvasNodeData, ReactFlowCanvasEdgeData>()
  // Subscribe only to structural node changes that affect group layout.
  // Viewport-only store updates (wheel panning, zoom) should not rebuild this
  // derived list or re-render every group node.
  const allNodes = useStore(
    selectGroupRenderNodes,
    areGroupRenderNodeSummariesEqual,
  )
  const canvas = useCanvasOptional()
  const node = useCanvasNode(id, 'nodeGroup', data)
  const { lightweightNodeChrome } = useCanvasNodeChromeMode()
  const uploadFile = useUploadFile()
  const { handleAddToChat } = useNodeActions({ nodeId: id })
  const setHoveredNodeId = useSetHoveredNodeId()

  // Read fields from CanvasNode wrapper
  const in_child_node_ids = node?.get('in_child_node_ids') ?? EMPTY_GROUP_CHILD_NODE_IDS
  const in_layout_mode = node?.get('in_layout_mode') as string | undefined
  const in_label = node?.get('in_label') as string | undefined
  const in_bg_color = node?.get('in_bg_color') as string | undefined
  const in_collapsible = node?.get('in_collapsible') === true
  const in_collapsed = in_collapsible && node?.get('in_collapsed') === true
  const groupLabel = (dataStrOpt(data, 'label'))
  const [isHovered, setIsHovered] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showRefSelector, setShowRefSelector] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const colorPickerRef = useRef<HTMLDivElement>(null)
  const [refSelectorInsertIndex, setRefSelectorInsertIndex] = useState(0)

  // Drop target state from canvas drag handler
  const dropTargetCtx = useGroupDropTarget()
  const isDropTarget = dropTargetCtx?.dropTarget.targetGroupId === id
  const dropRejection = isDropTarget ? dropTargetCtx?.dropTarget.rejection : null
  const insertionIndex = isDropTarget ? (dropTargetCtx?.dropTarget.insertionIndex ?? 0) : -1
  const dropAction = isDropTarget ? dropTargetCtx?.dropTarget.dropAction : null

  // Drag-out visual state
  const dragOutState = dropTargetCtx?.dropTarget.dragOutState
  const isDragOutTarget = dragOutState?.sourceGroupId === id
  const dragOutFactor = isDragOutTarget ? dragOutState!.distanceFactor : 0

  const effectiveDragId = dropTargetCtx?.activeDragNodeId || null

  // "Removed..." flash lifecycle: pick up from context, drive local fade-out timer
  const [localRemovedFlash, setLocalRemovedFlash] = useState<{
    nodeName: string
    groupName: string
  } | null>(null)

  const removedFlash = dropTargetCtx?.dropTarget.removedFlash
  useEffect(() => {
    if (removedFlash && removedFlash.sourceGroupId === id) {
      setLocalRemovedFlash({ nodeName: removedFlash.nodeName, groupName: removedFlash.groupName })
      dropTargetCtx?.setRemovedFlash(null)
      const timer = setTimeout(() => setLocalRemovedFlash(null), 1500)
      return () => clearTimeout(timer)
    }
  }, [removedFlash, id, dropTargetCtx])

  // Resolve child nodes from the generated reader; it returns a fresh typed array.
  const childNodeIds = in_child_node_ids
  const actualChildIds = childNodeIds.filter((cid): cid is string => cid !== null)

  // Resolve display name. "Video Group 1" / "Storyboard Group 1" are
  // Director-assigned defaults stored in English (the backend re-parses them
  // for ordinals and @-mention lookup), so they are localized on the way out
  // only. Returns undefined once the user renames, leaving the existing
  // in_label → label → 'Group' precedence untouched.
  const localizedDefaultLabel = localizeDefaultNodeName(
    'nodeGroup',
    { in_label, name_autogen: node?.get('name_autogen') },
    t,
  )
  const groupName = localizedDefaultLabel || in_label || groupLabel || 'Group'

  // Rename handler — local update + Y doc persistence
  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_label: newName } } : n,
        ),
      )
      node?.writeInLabel(newName)
    },
    [id, setNodes, node],
  )

  // Layout mode change handler — local update + Y doc persistence + reposition children
  const handleLayoutModeChange = useCallback(
    (newMode: LayoutMode) => {
      setNodes((nds) =>
        deriveGroupedNodeLayout(
          nds.map((n) => {
            if (n.id === id) {
              return { ...n, data: { ...n.data, in_layout_mode: newMode } }
            }
            return n
          }),
          [id],
        ),
      )
      node?.writeInLayoutMode(newMode)
    },
    [id, setNodes, node],
  )

  const handleBgColorChange = useCallback(
    (colorKey: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_bg_color: colorKey } } : n,
        ),
      )
      node?.writeInBgColor(colorKey)
      setShowColorPicker(false)
    },
    [id, setNodes, node],
  )

  const handleToggleCollapse = useCallback(() => {
    const nextCollapsed = !in_collapsed
    setNodes((nds) =>
      deriveGroupedNodeLayout(
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, in_collapsed: nextCollapsed } }
            : n,
        ),
        [id],
      ),
    )
    canvas?.updateNodeData({ id, type: 'nodeGroup' }, { in_collapsed: nextCollapsed })
  }, [canvas, id, in_collapsed, setNodes])

  // Close color picker on outside click
  useEffect(() => {
    if (!showColorPicker) return
    const handleClickOutside = (e: MouseEvent) => {
      if (colorPickerRef.current && e.target instanceof Node && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false)
      }
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [showColorPicker])

  // Close color picker when node is deselected
  useEffect(() => {
    if (!selected) setShowColorPicker(false)
  }, [selected])

  // One-time organize handler — compacts all gaps
  const handleOrganize = useCallback(() => {
    const currentIds = in_child_node_ids
    const hasGaps = currentIds.some((cid) => cid === null)
    if (!hasGaps) return

    const compactedIds = currentIds.filter((cid): cid is string => cid !== null)

    setNodes((nds) =>
      deriveGroupedNodeLayout(
        nds.map((n) => {
          if (n.id === id) {
            return {
              ...n,
              data: { ...n.data, in_child_node_ids: compactedIds },
            }
          }
          return n
        }),
        [id],
      ),
    )

    canvas?.updateNodeData({ id, type: 'nodeGroup' }, { in_child_node_ids: compactedIds })
  }, [id, in_child_node_ids, setNodes, canvas])

  // Single entry point for deleting this group. The keyboard Delete/Backspace
  // shortcut and the toolbar Delete button both call this so they show the
  // identical GroupDeleteDialog confirmation and run the identical downstream
  // removal (keep-children vs delete-all cascade). No duplicated deletion logic.
  const handleDeleteRequest = useCallback(
    (trigger: 'keyboard' | 'toolbar') => {
      const childCount = in_child_node_ids.filter((cid) => cid !== null).length
      groupDeleteLog({ event: 'delete-requested', groupId: id, trigger, childCount })
      setShowDeleteDialog(true)
    },
    [id, in_child_node_ids],
  )

  // Shared click handler for all empty slots — opens ReferenceSelector
  const handleEmptySlotClick = useCallback((insertIndex: number) => {
    setRefSelectorInsertIndex(insertIndex)
    setShowRefSelector(true)
  }, [])

  // Remove an empty (null) slot from in_child_node_ids
  const handleRemoveEmptySlot = useCallback(
    (slotIndex: number) => {
      if (!canvas) return

      const currentIds = [...in_child_node_ids]
      if (slotIndex < 0 || slotIndex >= currentIds.length || currentIds[slotIndex] !== null) return

      // Splice out the null at this index
      currentIds.splice(slotIndex, 1)

      // Trim trailing nulls
      while (currentIds.length > 0 && currentIds[currentIds.length - 1] === null) {
        currentIds.pop()
      }

      // Recompute layout and reposition children
      setNodes((nds) =>
        deriveGroupedNodeLayout(
          nds.map((n) => {
            if (n.id === id) {
              return { ...n, data: { ...n.data, in_child_node_ids: currentIds } }
            }
            return n
          }),
          [id],
        ),
      )

      canvas.updateNodeData({ id, type: 'nodeGroup' }, { in_child_node_ids: currentIds })
    },
    [canvas, id, in_child_node_ids, setNodes],
  )

  const groupAddNodeFilter = useCallback((candidateNode: ReactFlowCanvasNode) => {
    if (candidateNode.id === id) return false
    if (candidateNode.parentId) return false
    if (in_child_node_ids.includes(candidateNode.id)) return false
    return isGroupableNodeType(candidateNode.type)
  }, [id, in_child_node_ids])

  // Save selected standalone nodes into this group.
  const handleGroupNodeSelectionsSave = useCallback((references: SelectedNodeReference[]) => {
    setShowRefSelector(false)
    if (!canvas || references.length === 0) return

    const currentNodes = getNodes()
    const currentIds = [...in_child_node_ids]
    const addTraceId = `group-node-add:${id}`

    groupNodeAddLog({
      event: 'group-node-add-start',
      trace_id: addTraceId,
      groupId: id,
      insertIndex: refSelectorInsertIndex,
      selectionCount: references.length,
      selectedNodeIds: references.map((r) => r.nodeId).join(','),
      childIdsBefore: currentIds.join(','),
    })

    try {
      const absorbedIds: string[] = []
      let insertIdx = refSelectorInsertIndex
      for (const ref of references) {
        const refNode = currentNodes.find((n) => n.id === ref.nodeId)
        if (!refNode) {
          groupNodeAddLog({ event: 'group-node-add-skip', trace_id: addTraceId, groupId: id, nodeId: ref.nodeId, reason: 'not-on-canvas' })
          continue
        }
        if (refNode.id === id) {
          groupNodeAddLog({ event: 'group-node-add-skip', trace_id: addTraceId, groupId: id, nodeId: ref.nodeId, reason: 'self' })
          continue
        }
        if (refNode.parentId) {
          groupNodeAddLog({ event: 'group-node-add-skip', trace_id: addTraceId, groupId: id, nodeId: ref.nodeId, reason: 'already-parented', parentId: refNode.parentId })
          continue
        }
        if (currentIds.includes(ref.nodeId)) {
          groupNodeAddLog({ event: 'group-node-add-skip', trace_id: addTraceId, groupId: id, nodeId: ref.nodeId, reason: 'already-child' })
          continue
        }
        if (!isGroupableNodeType(refNode.type)) {
          groupNodeAddLog({ event: 'group-node-add-skip', trace_id: addTraceId, groupId: id, nodeId: ref.nodeId, reason: 'not-groupable', nodeType: refNode.type })
          continue
        }

        if (insertIdx < currentIds.length && currentIds[insertIdx] === null) {
          currentIds[insertIdx] = ref.nodeId
        } else {
          currentIds.splice(insertIdx, 0, ref.nodeId)
        }
        absorbedIds.push(ref.nodeId)
        insertIdx++
      }

      if (absorbedIds.length === 0) {
        groupNodeAddLog({
          event: 'group-node-add-commit',
          trace_id: addTraceId,
          groupId: id,
          atomic: false,
          absorbedCount: 0,
          absorbedIds: '',
          childIdsAfter: currentIds.join(','),
        })
        return
      }

      const absorbedIdSet = new Set(absorbedIds)
      setNodes((nds) =>
        deriveGroupedNodeLayout(
          nds.map((n) => {
            if (n.id === id) {
              return { ...n, data: { ...n.data, in_child_node_ids: currentIds } }
            }
            if (absorbedIdSet.has(n.id)) {
              return { ...n, parentId: id, extent: undefined }
            }
            return n
          }),
          [id],
        ),
      )

      // Keep group membership and child parent pointers in one YDoc update.
      const writeMembership = () => {
        canvas.persistNodeData({ id, type: 'nodeGroup' }, { in_child_node_ids: currentIds })
        for (const nodeId of absorbedIds) {
          canvas.updateNodeParent(nodeId, id)
        }
      }
      if (canvas.transact) {
        canvas.transact(writeMembership)
      } else {
        writeMembership()
      }

      // ReactFlow can keep a stale absolute position after parentId changes.
      // Re-derive next frame after it has rebuilt the parent-child relation.
      requestAnimationFrame(() => {
        setNodes((nds) => deriveGroupedNodeLayout(nds, [id]))
      })

      groupNodeAddLog({
        event: 'group-node-add-commit',
        trace_id: addTraceId,
        groupId: id,
        atomic: Boolean(canvas.transact),
        absorbedCount: absorbedIds.length,
        absorbedIds: absorbedIds.join(','),
        childIdsAfter: currentIds.join(','),
      })
    } catch (error) {
      groupNodeAddLog({
        event: 'group-node-add-error',
        trace_id: addTraceId,
        groupId: id,
        errorName: error instanceof Error ? error.name : typeof error,
        errorMessage: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }, [canvas, id, in_child_node_ids, refSelectorInsertIndex, setNodes, getNodes])

  // Container sizing based on child count and layout mode
  const layoutMode = normalizeLayoutMode(in_layout_mode)

  // Toolbar actions — collapse, layout mode buttons, and rearrange action
  const hasGaps = childNodeIds.some((cid) => cid === null)
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      ...(in_collapsible ? [{
        key: 'collapse',
        label: in_collapsed ? t('groupNode.expand') : t('groupNode.collapse'),
        icon: (
          <ChevronDown
            size={14}
            style={{ transform: in_collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
          />
        ),
        onClick: handleToggleCollapse,
      }] : []),
      {
        key: 'layout-horizontal',
        label: t('groupNode.horizontal'),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16" />
          </svg>
        ),
        onClick: () => handleLayoutModeChange('horizontal'),
        variant: layoutMode === 'horizontal' ? 'primary' : 'default',
      },
      {
        key: 'layout-vertical',
        label: t('groupNode.vertical'),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16M6 4v16" />
          </svg>
        ),
        onClick: () => handleLayoutModeChange('vertical'),
        variant: layoutMode === 'vertical' ? 'primary' : 'default',
      },
      {
        key: 'layout-auto',
        label: t('groupNode.autoGrid'),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h4v4H4V5zm6 0h4v4h-4V5zm6 0h4v4h-4V5zM4 11h4v4H4v-4zm6 0h4v4h-4v-4z" />
          </svg>
        ),
        onClick: () => handleLayoutModeChange('auto'),
        variant: layoutMode === 'auto' ? 'primary' : 'default',
      },
      {
        key: 'bg-color',
        label: t('groupNode.backgroundColor'),
        icon: <Palette size={14} />,
        onClick: () => setShowColorPicker((prev) => !prev),
        group: true,
      },
      {
        key: 'addToChat',
        label: t('groupNode.addToChat'),
        icon: <AddToChatIcon size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'organize',
        label: t('groupNode.organize'),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        ),
        onClick: handleOrganize,
        variant: 'default',
        group: true,
        disabled: !hasGaps,
      },
      {
        key: 'delete',
        label: t('groupNode.delete'),
        icon: <Trash2 size={14} />,
        onClick: () => handleDeleteRequest('toolbar'),
        variant: 'danger',
        group: true,
      },
    ],
    [t, in_collapsible, in_collapsed, layoutMode, hasGaps, handleToggleCollapse, handleLayoutModeChange, handleOrganize, handleAddToChat, handleDeleteRequest],
  )

  // Hover handlers
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
    setHoveredNodeId(id)
  }, [id, setHoveredNodeId])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
    setHoveredNodeId(null)
  }, [setHoveredNodeId])

  // Intercept Delete/Backspace when group is selected — show confirmation dialog
  useEffect(() => {
    if (!selected) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return

      // A blocking overlay is open over the canvas — suspend group deletion too,
      // regardless of focus. See canvasShortcutGuard / WorkspacePageV2.
      if (isBlockingOverlayOpen()) {
        overlayGuardLog({
          event: 'canvas-shortcut:suspended-blocking-overlay',
          feature: 'canvas-shortcut-overlay-guard',
          trace_id: 'canvas-shortcut-overlay-guard:group',
          key: e.key,
          handler: 'group-capture',
          overlayOpen: true,
        })
        return
      }

      // Skip when user is editing text (e.g., FloatingNodeLabel input)
      const active = document.activeElement
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active?.getAttribute('contenteditable') === 'true'
      ) {
        return
      }

      // Prevent ReactFlow's default delete behavior
      e.stopPropagation()
      e.preventDefault()
      handleDeleteRequest('keyboard')
    }

    // Use capture phase to intercept before ReactFlow's handler
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [selected, handleDeleteRequest])

  const handleKeepChildren = useCallback(() => {
    setShowDeleteDialog(false)
    groupDeleteLog({
      event: 'delete-confirmed',
      groupId: id,
      mode: 'keep-children',
      childCount: childNodeIds.filter((cid) => cid !== null).length,
    })

    // Get group's absolute position
    const groupNode = getNode(id)
    if (!groupNode) return

    const groupPos = groupNode.position
    const childUpdates = childNodeIds
      .filter((childId): childId is string => childId !== null)
      .map((childId) => {
        const child = getNode(childId)
        if (!child) return null
        return {
          id: childId,
          x: groupPos.x + child.position.x,
          y: groupPos.y + child.position.y,
        }
      })
      .filter((child): child is { id: string; x: number; y: number } => child !== null)

    // Update children: remove parentId, convert to absolute positions
    setNodes((nds) =>
      nds.map((n) => {
        if (childNodeIds.includes(n.id)) {
          return {
            ...n,
            parentId: undefined,
            position: {
              x: groupPos.x + n.position.x,
              y: groupPos.y + n.position.y,
            },
          }
        }
        return n
      })
    )
    const persistChildrenAndRemoveGroup = () => {
      for (const child of childUpdates) {
        canvas?.updateNodeParent(child.id, null)
        canvas?.updateNodePosition(child.id, child.x, child.y)
      }

      // Remove the group node (now empty) and its edges
      canvas?.removeNode(id)
    }
    if (canvas?.transact) {
      canvas.transact(persistChildrenAndRemoveGroup)
    } else {
      persistChildrenAndRemoveGroup()
    }
  }, [id, childNodeIds, getNode, setNodes, canvas])

  const handleDeleteAll = useCallback(() => {
    setShowDeleteDialog(false)

    // Collect all node IDs to remove: children + group itself
    const idsToRemove = new Set<string>()
    for (const childId of childNodeIds) {
      if (childId !== null) idsToRemove.add(childId)
    }
    idsToRemove.add(id)

    groupDeleteLog({
      event: 'delete-confirmed',
      groupId: id,
      mode: 'delete-all',
      childCount: idsToRemove.size - 1,
      idsToRemove: Array.from(idsToRemove),
    })

    canvas?.removeNodes?.(Array.from(idsToRemove))
  }, [id, childNodeIds, canvas])

  // Border states per spec:
  // - Default: 1px solid rgba(255,255,255,0.15)
  // - Hover:   1px solid rgba(255,255,255,0.4)
  // - Selected: 2px solid #0074FE
  // Use padding compensation to prevent 1px layout shift between 1px and 2px borders.
  const isSelected = !!selected
  const borderStyle: React.CSSProperties = (() => {
    // Drag-out red gradient (highest priority during active drag-out)
    if (isDragOutTarget && dragOutFactor > 0) {
      return {
        border: `2px solid rgba(255, 61, 0, ${dragOutFactor})`,
        padding: CONTAINER_PADDING,
      }
    }
    // Drop target states (highest priority after selection)
    if (isDropTarget && dropRejection === null) {
      return { border: '2px solid #00C853', padding: CONTAINER_PADDING }
    }
    if (isDropTarget && dropRejection === 'category_mismatch') {
      return { border: '2px solid #FF3D00', padding: CONTAINER_PADDING }
    }
    // 'unsupported' rejection: no highlight change — fall through to normal states
    if (isSelected) {
      return { border: '2px solid #0074FE', padding: CONTAINER_PADDING }
    }
    const colorBorder = in_bg_color && in_bg_color !== 'default' ? NODE_BG_SWATCH_COLORS[in_bg_color] : null
    if (isHovered) {
      return { border: `1px solid ${colorBorder ?? 'rgba(255,255,255,0.4)'}`, padding: CONTAINER_PADDING + 1 }
    }
    return { border: `1px solid ${colorBorder ? colorBorder + '80' : 'rgba(255,255,255,0.15)'}`, padding: CONTAINER_PADDING + 1 }
  })()

  // Group layout metrics only depend on child membership and child node data,
  // not viewport changes. Memoizing this avoids repeating size-registry lookups
  // when React Flow re-renders nodes during wheel pan / zoom.
  const { cellWidth, cellHeight, panelHeight } = useMemo(
    () => computeCellSize(actualChildIds, allNodes),
    [actualChildIds, allNodes],
  )

  const childDimensionKey = useMemo(
    () => actualChildIds
      .map((childId) => {
        const child = allNodes.find((node) => node.id === childId)
        return `${childId}:${child?.width ?? ''}x${child?.height ?? ''}`
      })
      .join('|'),
    [actualChildIds, allNodes],
  )

  useEffect(() => {
    if (actualChildIds.length === 0) return
    layoutLog('nodeGroup child dimensions changed groupId=%s dims=%s', id, childDimensionKey)
    setNodes((nodes) => deriveGroupedNodeLayout(nodes, [id]))
  }, [actualChildIds.length, childDimensionKey, id, setNodes])
  // Size for actual children only (no end-of-grid placeholder)
  const slotCountForSizing = childNodeIds.length || 1 // minimum 1 for empty state
  const expandedContainerSize = computeContainerSize(
    slotCountForSizing,
    layoutMode,
    cellWidth,
    cellHeight,
  )
  const containerWidth = in_collapsed ? COLLAPSED_GROUP_WIDTH : expandedContainerSize.width
  const containerHeight = in_collapsed ? COLLAPSED_GROUP_HEIGHT : expandedContainerSize.height
  const shouldRenderFlatGroup =
    !in_collapsed &&
    lightweightNodeChrome &&
    !selected &&
    !dragging &&
    !isHovered &&
    !isDropTarget &&
    !isDragOutTarget &&
    !localRemovedFlash &&
    !showColorPicker &&
    !showDeleteDialog &&
    !showRefSelector

  if (shouldRenderFlatGroup) {
    return (
      <div
        data-testid="group-node-flat"
        className={NODE_CARD_RADIUS_CLASS}
        style={{
          background: resolveNodeBgColor(in_bg_color),
          border: borderStyle.border,
          width: containerWidth,
          minHeight: containerHeight,
          padding: CONTAINER_PADDING + 1,
          // Anchors the label below. Safe to nest it here rather than in a
          // wrapper the way the media cards need: this div sets no
          // `overflow: hidden` and no containment, so the label can sit above
          // its top edge without being clipped.
          position: 'relative',
        }}
        onMouseEnter={handleMouseEnter}
      >
        <GroupEdgeProxyHandles />
        {/* Without this the group is an unlabelled rectangle when zoomed out —
          * worse than an unlabelled media node, since a group has no preview to
          * recognise it by either. Same component and name the full group uses;
          * read-only, because renaming needs the full group (hover restores
          * it). */}
        <FloatingNodeLabel
          name={groupName}
          fallback={t('groupNode.untitledGroup')}
          bgColor={in_bg_color && in_bg_color !== 'default' ? NODE_BG_SWATCH_COLORS[in_bg_color] : undefined}
        />
      </div>
    )
  }

  return (
    <div className="relative w-fit" data-testid="group-node">
      <GroupEdgeProxyHandles />
      {/* Inject fadeOut keyframes for removed flash */}
      <style>{`
        @keyframes groupNodeFadeOut {
          0% { opacity: 1; }
          70% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      {/* Floating label — top-left of container (spec §Naming reqs 6-8) */}
      <FloatingNodeLabel
        name={groupName}
        fallback={t('groupNode.untitledGroup')}
        onRename={handleRename}
        bgColor={in_bg_color && in_bg_color !== 'default' ? NODE_BG_SWATCH_COLORS[in_bg_color] : undefined}
      />

      {/* Drag-out overlay: "Removing {nodeName} from {groupName}"
          Positioned above the container (outside it) so it never requires z-index elevation. */}
      {isDragOutTarget && dragOutFactor > 0 && (
        <div
          data-testid="drag-out-overlay"
          style={{
            position: 'absolute',
            top: -8,
            right: 0,
            pointerEvents: 'none',
          }}
        >
          <span
            className="text-sm"
            style={{
              color: `rgba(255, 61, 0, ${dragOutFactor})`,
              fontWeight: 600,
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {t('groupNode.removingFromGroup', { nodeName: dragOutState!.nodeName, groupName: dragOutState!.groupName })}
          </span>
        </div>
      )}

      {/* Removed flash: "Removed {nodeName} from {groupName}"
          Positioned above the container (outside it) so it never requires z-index elevation. */}
      {localRemovedFlash && (
        <div
          data-testid="removed-flash"
          style={{
            position: 'absolute',
            top: -8,
            right: 0,
            pointerEvents: 'none',
            animation: 'groupNodeFadeOut 1.5s ease-out forwards',
          }}
        >
          <span
            className="text-sm"
            style={{
              color: '#00C853',
              fontWeight: 600,
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {t('groupNode.removedFromGroup', { nodeName: localRemovedFlash.nodeName, groupName: localRemovedFlash.groupName })}
          </span>
        </div>
      )}

      {/* Color picker panel — rendered outside the toolbar, like GroupDeleteDialog */}
      {showColorPicker && (
        <div
          ref={colorPickerRef}
          data-testid="group-color-picker"
          style={{
            position: 'absolute',
            top: -52,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 60,
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1.5 rounded-full bg-surface-2 backdrop-blur-md border border-border shadow-xl px-3 py-2">
            {Object.entries(NODE_BG_SWATCH_COLORS).map(([key, swatchColor]) => {
              const activeBgKey = in_bg_color
              const isActive = (activeBgKey ?? 'default') === key
              return (
                <button
                  key={key}
                  aria-label={localizeNodeBgColor(key, t)}
                  title={localizeNodeBgColor(key, t)}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleBgColorChange(key)
                  }}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: swatchColor,
                    border: isActive ? '2px solid white' : '2px solid transparent',
                    cursor: 'pointer',
                    flexShrink: 0,
                    outline: 'none',
                    boxShadow: isActive ? '0 0 0 1px rgba(255,255,255,0.3)' : 'none',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                  }}
                />
              )
            })}
          </div>
        </div>
      )}

      {/* Main container — children are rendered by ReactFlow via parentId */}
      <div
        data-testid="group-node-container"
        style={{
          background: resolveNodeBgColor(in_bg_color),
          borderRadius: NODE_CARD_RADIUS_PX,
          width: containerWidth,
          minHeight: containerHeight,
          transition: 'border-color 0.15s ease',
          position: 'relative',
          ...borderStyle,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {!in_collapsed && actualChildIds.length === 0 && (
          <div
            style={{
              position: 'absolute',
              left: CONTAINER_PADDING,
              top: CONTAINER_PADDING + CHILD_LABEL_HEIGHT,
              zIndex: 10,
            }}
          >
            <AddPlaceholderSlot
              width={cellWidth}
              height={cellHeight}
              insertIndex={0}
              onClick={handleEmptySlotClick}
            />
          </div>
        )}

        {/* n+1 add buttons — visible on hover, positioned at midpoints/edges */}
        {!in_collapsed && isHovered && actualChildIds.length > 0 && (() => {
          // Build descriptors for n+1 buttons (before first, between each pair, after last)
          const buttonDescriptors: { insertIndex: number; x: number; y: number }[] = []

          // Get positions and indices of actual (non-null) children in the childNodeIds array
          const actualEntries = childNodeIds
            .map((cid, idx) => ({ cid, idx }))
            .filter((e): e is { cid: string; idx: number } => e.cid !== null)

          // Button before first child
          if (actualEntries.length > 0) {
            const firstPos = computeChildPosition(actualEntries[0].idx, layoutMode, cellWidth, cellHeight)
            if (layoutMode === 'vertical') {
              // Above first child, horizontally centered
              buttonDescriptors.push({
                insertIndex: 0,
                x: firstPos.x + cellWidth / 2,
                y: firstPos.y - CHILD_GAP / 2,
              })
            } else {
              // Left of first child, vertically centered
              buttonDescriptors.push({
                insertIndex: 0,
                x: firstPos.x - CHILD_GAP / 2,
                y: firstPos.y + cellHeight / 2,
              })
            }
          }

          // Buttons between consecutive actual children
          for (let ai = 0; ai < actualEntries.length - 1; ai++) {
            const curr = actualEntries[ai]
            const next = actualEntries[ai + 1]
            const pos1 = computeChildPosition(curr.idx, layoutMode, cellWidth, cellHeight)
            const pos2 = computeChildPosition(next.idx, layoutMode, cellWidth, cellHeight)
            const rc1 = getRowCol(curr.idx, layoutMode)
            const rc2 = getRowCol(next.idx, layoutMode)

            if (layoutMode === 'vertical') {
              // Vertical: midpoint between stacked children
              buttonDescriptors.push({
                insertIndex: next.idx,
                x: (pos1.x + cellWidth / 2 + pos2.x + cellWidth / 2) / 2,
                y: (pos1.y + cellHeight + pos2.y) / 2,
              })
            } else if (rc1.row === rc2.row) {
              // Same row: midpoint between adjacent horizontal children
              buttonDescriptors.push({
                insertIndex: next.idx,
                x: (pos1.x + cellWidth + pos2.x) / 2,
                y: (pos1.y + cellHeight / 2 + pos2.y + cellHeight / 2) / 2,
              })
            } else {
              // Row break: button to the right of the last child in the current row
              buttonDescriptors.push({
                insertIndex: next.idx,
                x: pos1.x + cellWidth + CHILD_GAP / 2,
                y: pos1.y + cellHeight / 2,
              })
            }
          }

          // Button after last child
          if (actualEntries.length > 0) {
            const lastEntry = actualEntries[actualEntries.length - 1]
            const lastPos = computeChildPosition(lastEntry.idx, layoutMode, cellWidth, cellHeight)

            const lastRowCol = getRowCol(lastEntry.idx, layoutMode)
            const isLastColFull = layoutMode === 'auto' && lastRowCol.col === COLUMNS - 1

            if (layoutMode === 'vertical' || isLastColFull) {
              // Below: start of next row (or below in vertical mode)
              const nextRowFirstPos = computeChildPosition(
                childNodeIds.length,
                layoutMode,
                cellWidth,
                cellHeight,
              )
              buttonDescriptors.push({
                insertIndex: childNodeIds.length,
                x: nextRowFirstPos.x + cellWidth / 2,
                y: lastPos.y + cellHeight + CHILD_GAP / 2,
              })
            } else {
              // Right of last child, vertically centered
              buttonDescriptors.push({
                insertIndex: childNodeIds.length,
                x: lastPos.x + cellWidth + CHILD_GAP / 2,
                y: lastPos.y + cellHeight / 2,
              })
            }
          }

          return buttonDescriptors.map((desc, bi) => (
            <div
              key={`add-btn-${bi}`}
              style={{
                position: 'absolute',
                left: desc.x - 9,
                top: desc.y - 9,
                zIndex: 10,
              }}
            >
              <AddPanelButton
                insertIndex={desc.insertIndex}
                onClick={handleEmptySlotClick}
              />
            </div>
          ))
        })()}

        {/* Gap-fill empty slots — always visible when gaps exist */}
        {!in_collapsed && childNodeIds.some((cid) => cid === null) && (
          <>
            {childNodeIds.map((childId, i) => {
              if (childId !== null) return null
              const pos = computeChildPosition(i, layoutMode, cellWidth, cellHeight)

              return (
                <div
                  key={`gap-${i}`}
                  style={{
                    position: 'absolute',
                    left: pos.x,
                    top: pos.y,
                    zIndex: 10,
                  }}
                >
                  <AddPlaceholderSlot
                    width={cellWidth}
                    height={panelHeight}
                    insertIndex={i}
                    onClick={handleEmptySlotClick}
                    onRemove={() => handleRemoveEmptySlot(i)}
                  />
                </div>
              )
            })}
          </>
        )}

        {/* Insertion indicator — shows where the dragged node will land */}
        {!in_collapsed && isDropTarget && dropRejection === null && dropAction !== 'replace' && (() => {
          const indicatorPos = computeChildPosition(insertionIndex, layoutMode, cellWidth, cellHeight)
          const isVerticalLayout = layoutMode === 'vertical'
          return (
            <div
              data-testid="insertion-indicator"
              style={{
                position: 'absolute',
                left: isVerticalLayout
                  ? indicatorPos.x
                  : indicatorPos.x - 2,
                top: isVerticalLayout
                  ? indicatorPos.y - 2
                  : indicatorPos.y,
                width: isVerticalLayout ? cellWidth : 4,
                height: isVerticalLayout ? 4 : cellHeight,
                background: '#00C853',
                borderRadius: 2,
                pointerEvents: 'none',
                zIndex: 10,
                transition: 'left 0.15s ease, top 0.15s ease',
              }}
            />
          )
        })()}

        {/* Replace indicator — highlights the empty slot being replaced */}
        {!in_collapsed && isDropTarget && dropRejection === null && dropAction === 'replace' && (() => {
          const replacePos = computeChildPosition(insertionIndex, layoutMode, cellWidth, cellHeight)
          return (
            <div
              data-testid="replace-indicator"
              style={{
                position: 'absolute',
                left: replacePos.x,
                top: replacePos.y,
                width: cellWidth,
                height: cellHeight,
                border: '2px solid #00C853',
                borderRadius: 8,
                background: 'rgba(0, 200, 83, 0.08)',
                pointerEvents: 'none',
                zIndex: 10,
                transition: 'left 0.15s ease, top 0.15s ease',
              }}
            />
          )
        })()}

        {/* Ghost placeholder — shows at the vacated slot during cross-group drag only.
            Hidden during within-group drag because the live reorder preview fills the gap. */}
        {!in_collapsed && effectiveDragId && childNodeIds.includes(effectiveDragId) && !actualChildIds.includes(effectiveDragId) && (() => {
          const ghostIndex = childNodeIds.indexOf(effectiveDragId)
          if (ghostIndex === -1) return null
          const ghostPos = computeChildPosition(ghostIndex, layoutMode, cellWidth, cellHeight)
          return (
            <div
              data-testid="ghost-placeholder"
              style={{
                position: 'absolute',
                left: ghostPos.x,
                top: ghostPos.y,
                width: cellWidth,
                height: cellHeight,
                background: 'rgba(255,255,255,0.03)',
                border: '2px dashed rgba(255,255,255,0.15)',
                borderRadius: 8,
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
          )
        })()}

      </div>

      <GroupDeleteDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onKeepChildren={handleKeepChildren}
        onDeleteAll={handleDeleteAll}
        childCount={actualChildIds.length}
      />

      <ReferenceSelector
        isOpen={showRefSelector}
        onClose={() => setShowRefSelector(false)}
        onSave={() => {}}
        excludeNodeId={id}
        canvasNodes={getNodes()}
        maxReferences={99}
        mode="node-selector"
        nodeSelectorFilter={groupAddNodeFilter}
        onSaveNodeSelections={handleGroupNodeSelectionsSave}
        nodeSelectorTitle={t('referenceSelector.addNodesToGroup')}
        nodeSelectorEmptyLabel={t('referenceSelector.noGroupableNodes')}
        nodeSelectorNoSelectionLabel={t('referenceSelector.noGroupNodesSelected')}
        onUploadFile={uploadFile ?? undefined}
      />
    </div>
  )
})

GroupNode.displayName = 'GroupNode'
export { GroupNode }
